import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend,
  Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis
} from 'recharts'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Modal } from '../../components/atoms/Modal'
import { Spinner } from '../../components/atoms/Spinner'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import {
  useTrafficOverview, useTrafficAttribution, useTrafficRealtime,
  useTrafficFunnel, useTrafficPages, useTrafficGeo,
  useTrafficDevices, useTrafficCohorts, useTrafficFraud,
  useTrafficSessions, useTrafficSessionDetail
} from '../../../viewmodels/useTrafficAnalytics'

const TABS = [
  { id: 'overview',    label: 'Overview' },
  { id: 'attribution', label: 'Attribution' },
  { id: 'realtime',    label: 'Real-time' },
  { id: 'funnel',      label: 'Funnel' },
  { id: 'pages',       label: 'Pages' },
  { id: 'geo',         label: 'Geo' },
  { id: 'devices',     label: 'Devices' },
  { id: 'cohorts',     label: 'Cohorts' },
  { id: 'fraud',       label: 'Fraud' },
  { id: 'sessions',    label: 'Sessions' }
] as const
type TabId = typeof TABS[number]['id']

const PALETTE: readonly string[] = ['#BC7E5E', '#5E8D6D', '#8E6DBB', '#C97A4A', '#4A8AA6', '#D4A85F', '#7DA167', '#B4635A', '#6A6A6A', '#96754F']
function colour(i: number): string { return PALETTE[i % PALETTE.length] ?? '#666' }

/**
 * /dashboard/traffic — storefront visitor analytics (Kiran 2026-07-02).
 * Ten tabs, one page. Each tab loads its own data on mount so the initial
 * paint is fast. Reused KPI cards, tables, and colour palette across tabs.
 */
export function TrafficDashboardPage(): JSX.Element {
  const [tab, setTab] = useState<TabId>('overview')
  return (
    <>
      <Helmet><title>Traffic — Uyyamadilu</title></Helmet>
      <header className="mb-6">
        <h1 className="font-heading text-h1 text-charcoal">Traffic</h1>
        <p className="mt-1 font-body text-body-sm text-text-secondary">
          Storefront visitor analytics. Overview, attribution, real-time, funnel, pages, geo,
          devices, cohorts, fraud alerts, and per-session drill-down.
        </p>
      </header>

      <TabStrip active={tab} onSelect={setTab} />

      <div className="mt-6">
        {tab === 'overview'    ? <OverviewTab    /> : null}
        {tab === 'attribution' ? <AttributionTab /> : null}
        {tab === 'realtime'    ? <RealtimeTab    /> : null}
        {tab === 'funnel'      ? <FunnelTab      /> : null}
        {tab === 'pages'       ? <PagesTab       /> : null}
        {tab === 'geo'         ? <GeoTab         /> : null}
        {tab === 'devices'     ? <DevicesTab     /> : null}
        {tab === 'cohorts'     ? <CohortsTab     /> : null}
        {tab === 'fraud'       ? <FraudTab       /> : null}
        {tab === 'sessions'    ? <SessionsTab    /> : null}
      </div>
    </>
  )
}

/* ─────────────── Tab strip ─────────────── */

function TabStrip({ active, onSelect }: { active: TabId; onSelect: (t: TabId) => void }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1 border-b border-border">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onSelect(t.id)}
          className={`px-3 py-2 font-body text-body-sm border-b-2 transition-colors ${
            active === t.id
              ? 'border-forest text-charcoal font-medium'
              : 'border-transparent text-text-secondary hover:text-charcoal'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

/* ─────────────── Shared UI atoms ─────────────── */

function KpiCard({ label, value, hint }: { label: string; value: string | number; hint?: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-parchment/40 p-4">
      <p className="font-body text-body-xs uppercase tracking-wide text-text-secondary">{label}</p>
      <p className="mt-1 font-heading text-h3 text-charcoal">{value}</p>
      {hint !== undefined ? <p className="mt-1 font-body text-body-xs text-text-secondary">{hint}</p> : null}
    </div>
  )
}

function LoadingBlock(): JSX.Element {
  return <div className="flex items-center justify-center py-12"><Spinner /></div>
}
function ErrorBlock({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <Alert tone="error">
      {message}{' '}
      <button type="button" className="underline underline-offset-2 hover:text-charcoal" onClick={onRetry}>Retry</button>
    </Alert>
  )
}

function pct(n: number): string { return `${n.toFixed(1)}%` }

/* ─────────────── 1. Overview ─────────────── */

function OverviewTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficOverview()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  const { overview, hourly } = data
  const delta = overview.sessions_yesterday === 0 ? 0
    : ((overview.sessions_today - overview.sessions_yesterday) / overview.sessions_yesterday) * 100
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard label="Sessions today" value={overview.sessions_today} hint={`${delta >= 0 ? '+' : ''}${delta.toFixed(1)}% vs yesterday`} />
        <KpiCard label="Pageviews today" value={overview.pageviews_today} />
        <KpiCard label="Signed in / Anon" value={`${overview.signed_in_today} / ${overview.anon_today}`} />
        <KpiCard label="Bot %" value={pct(overview.bot_share_pct)} />
        <KpiCard label="Orders today" value={overview.orders_today} />
        <KpiCard label="Conversion" value={pct(overview.conversion_pct)} hint="Orders / sessions" />
      </div>
      <div className="rounded-md border border-border bg-bg-primary p-4">
        <p className="font-body text-body-sm text-text-secondary mb-3">Last 24 hours</p>
        <div className="h-56">
          <ResponsiveContainer>
            <AreaChart data={hourly.map((h) => ({ hour: h.hour.slice(11, 16), sessions: h.sessions, pageviews: h.pageviews }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="hour" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Area type="monotone" dataKey="sessions" stroke={colour(0)} fill={colour(0)} fillOpacity={0.2} />
              <Area type="monotone" dataKey="pageviews" stroke={colour(1)} fill={colour(1)} fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

/* ─────────────── 2. Attribution ─────────────── */

function AttributionTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficAttribution()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <section>
        <h2 className="font-heading text-h3 text-charcoal mb-3">Top referers ({data.days}d)</h2>
        <TableShell>
          <table className="w-full font-body text-body-sm">
            <thead className="bg-parchment/50 text-text-secondary">
              <tr><Th>Host</Th><Th className="text-right">Sessions</Th></tr>
            </thead>
            <tbody>
              {data.referers.map((r) => (
                <tr key={r.key} className="border-t border-border">
                  <Td className="font-mono">{r.key}</Td>
                  <Td className="text-right font-mono">{r.count}</Td>
                </tr>
              ))}
              {data.referers.length === 0 ? <tr><td className="p-3 text-text-secondary" colSpan={2}>No sessions in this window.</td></tr> : null}
            </tbody>
          </table>
        </TableShell>
      </section>
      <section>
        <h2 className="font-heading text-h3 text-charcoal mb-3">Top UTM campaigns ({data.days}d)</h2>
        <TableShell>
          <table className="w-full font-body text-body-sm">
            <thead className="bg-parchment/50 text-text-secondary">
              <tr>
                <Th>Source</Th><Th>Medium</Th><Th>Campaign</Th>
                <Th className="text-right">Sessions</Th><Th className="text-right">Orders</Th><Th className="text-right">Conv %</Th>
              </tr>
            </thead>
            <tbody>
              {data.utm.map((u, i) => (
                <tr key={`${u.source}-${u.medium}-${u.campaign}-${i}`} className="border-t border-border">
                  <Td>{u.source}</Td><Td>{u.medium}</Td><Td>{u.campaign}</Td>
                  <Td className="text-right font-mono">{u.sessions}</Td>
                  <Td className="text-right font-mono">{u.orders}</Td>
                  <Td className="text-right font-mono">{u.conversion_pct.toFixed(2)}%</Td>
                </tr>
              ))}
              {data.utm.length === 0 ? <tr><td className="p-3 text-text-secondary" colSpan={6}>No UTM campaigns.</td></tr> : null}
            </tbody>
          </table>
        </TableShell>
      </section>
    </div>
  )
}

/* ─────────────── 3. Realtime ─────────────── */

function RealtimeTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficRealtime()
  // Auto-refresh every 10s.
  useAutoInterval(reload, 10_000)
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <p className="font-body text-body-sm text-text-secondary">
          Visitors seen in the last <span className="font-mono">{data.minutes}</span> minutes ·
          Auto-refresh 10s
        </p>
        <button type="button" className="font-body text-body-xs text-forest underline-offset-2 hover:underline" onClick={reload}>Refresh now</button>
      </div>
      <TableShell>
        <table className="w-full font-body text-body-sm">
          <thead className="bg-parchment/50 text-text-secondary">
            <tr>
              <Th>Ago</Th><Th>Session</Th><Th>Path</Th><Th>Country · City</Th><Th>Device</Th><Th>Auth</Th>
            </tr>
          </thead>
          <tbody>
            {data.visitors.map((v) => (
              <tr key={v.session_id} className="border-t border-border">
                <Td className="whitespace-nowrap font-mono text-body-xs text-text-secondary">{v.seconds_ago}s</Td>
                <Td className="font-mono text-body-xs">{v.session_id.slice(0, 8)}</Td>
                <Td className="font-mono max-w-[24ch] truncate" title={v.path ?? ''}>{v.path ?? '—'}</Td>
                <Td>{v.country ?? '—'}{v.city ? ` · ${v.city}` : ''}</Td>
                <Td>{v.device_type ?? '—'}{v.browser ? ` · ${v.browser}` : ''}</Td>
                <Td>{v.is_authenticated === 1 ? '✓' : ''}</Td>
              </tr>
            ))}
            {data.visitors.length === 0 ? <tr><td className="p-3 text-text-secondary" colSpan={6}>Nobody here right now.</td></tr> : null}
          </tbody>
        </table>
      </TableShell>
    </div>
  )
}

function useAutoInterval(fn: () => void, ms: number): void {
  useEffect(() => {
    const id = window.setInterval(fn, ms)
    return () => window.clearInterval(id)
  }, [fn, ms])
}

/* ─────────────── 4. Funnel ─────────────── */

const FUNNEL_LABEL: Record<string, string> = {
  '1_shop_list':      '/shop (list)',
  '2_product_detail': '/shop/:id (product)',
  '3_cart':           '/cart',
  '4_order_placed':   '/account/orders/:id'
}

function FunnelTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficFunnel()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  const top = data.stages[0]?.sessions ?? 0
  return (
    <div className="space-y-4">
      <p className="font-body text-body-sm text-text-secondary">Last {data.days} days · distinct sessions at each stage.</p>
      <div className="space-y-2">
        {data.stages.map((s, i) => {
          const share = top === 0 ? 0 : (s.sessions / top) * 100
          const dropFromPrev = i === 0 ? 0
            : ((data.stages[i - 1]!.sessions - s.sessions) / Math.max(1, data.stages[i - 1]!.sessions)) * 100
          return (
            <div key={s.stage} className="rounded-md border border-border bg-parchment/40 p-3">
              <div className="flex justify-between items-baseline">
                <span className="font-body text-body-sm text-charcoal">{FUNNEL_LABEL[s.stage] ?? s.stage}</span>
                <span className="font-mono text-body-sm">{s.sessions}</span>
              </div>
              <div className="mt-2 h-2 bg-bg-secondary rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${share}%`, background: colour(i) }} />
              </div>
              {i > 0 && data.stages[i - 1]!.sessions > 0 ? (
                <p className="mt-1 font-body text-body-xs text-text-secondary">
                  {dropFromPrev.toFixed(1)}% drop-off from previous step
                </p>
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ─────────────── 5. Pages ─────────────── */

function PagesTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficPages()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <section>
        <h2 className="font-heading text-h3 text-charcoal mb-3">Top pages ({data.days}d)</h2>
        <TableShell>
          <table className="w-full font-body text-body-sm">
            <thead className="bg-parchment/50 text-text-secondary">
              <tr><Th>Path</Th><Th className="text-right">Views</Th><Th className="text-right">Avg dwell</Th></tr>
            </thead>
            <tbody>
              {data.top.map((p) => (
                <tr key={p.path} className="border-t border-border">
                  <Td className="font-mono max-w-[30ch] truncate" title={p.path}>{p.path}</Td>
                  <Td className="text-right font-mono">{p.views}</Td>
                  <Td className="text-right font-mono">{p.avg_dwell_ms === null ? '—' : `${(p.avg_dwell_ms / 1000).toFixed(1)}s`}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      </section>
      <section>
        <h2 className="font-heading text-h3 text-charcoal mb-3">Top exit pages ({data.days}d)</h2>
        <TableShell>
          <table className="w-full font-body text-body-sm">
            <thead className="bg-parchment/50 text-text-secondary">
              <tr><Th>Path</Th><Th className="text-right">Exits</Th></tr>
            </thead>
            <tbody>
              {data.exits.map((p) => (
                <tr key={p.path} className="border-t border-border">
                  <Td className="font-mono max-w-[30ch] truncate" title={p.path}>{p.path}</Td>
                  <Td className="text-right font-mono">{p.exits}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      </section>
    </div>
  )
}

/* ─────────────── 6. Geo ─────────────── */

function GeoTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficGeo()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="space-y-6">
      <p className="font-body text-body-sm text-text-secondary">Last {data.days} days.</p>
      <div className="rounded-md border border-border bg-bg-primary p-4">
        <p className="font-body text-body-sm text-text-secondary mb-3">Sessions by country (top 15)</p>
        <div className="h-72">
          <ResponsiveContainer>
            <BarChart data={data.byCountry.slice(0, 15)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis type="number" fontSize={10} />
              <YAxis dataKey="key" type="category" width={80} fontSize={10} />
              <Tooltip />
              <Bar dataKey="count" fill={colour(0)} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <section>
        <h2 className="font-heading text-h3 text-charcoal mb-3">Top cities</h2>
        <TableShell>
          <table className="w-full font-body text-body-sm">
            <thead className="bg-parchment/50 text-text-secondary">
              <tr><Th>Country</Th><Th>City</Th><Th className="text-right">Sessions</Th></tr>
            </thead>
            <tbody>
              {data.byCity.map((c, i) => (
                <tr key={`${c.country}-${c.city}-${i}`} className="border-t border-border">
                  <Td>{c.country}</Td><Td>{c.city ?? '—'}</Td>
                  <Td className="text-right font-mono">{c.sessions}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      </section>
    </div>
  )
}

/* ─────────────── 7. Devices ─────────────── */

function DevicesTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficDevices()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <DevicePie title="Device type" data={data.devices} />
      <DevicePie title="Browser" data={data.browsers} />
      <DevicePie title="Operating system" data={data.operating_systems} />
    </div>
  )
}
function DevicePie({ title, data }: { title: string; data: ReadonlyArray<{ key: string; count: number }> }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-bg-primary p-4">
      <p className="font-body text-body-sm text-text-secondary mb-3">{title}</p>
      <div className="h-56">
        <ResponsiveContainer>
          <PieChart>
            <Pie dataKey="count" nameKey="key" data={[...data]} outerRadius={70} label>
              {data.map((_, i) => <Cell key={i} fill={colour(i)} />)}
            </Pie>
            <Tooltip />
            <Legend fontSize={10} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

/* ─────────────── 8. Cohorts ─────────────── */

function CohortsTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficCohorts()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="space-y-4">
      <p className="font-body text-body-sm text-text-secondary">Weekly cohorts · last {data.weeks} weeks. "New" = single-page visitors; "Returning" = multi-page.</p>
      <div className="rounded-md border border-border bg-bg-primary p-4">
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={[...data.cohorts].reverse()}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="cohort_week" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip /><Legend fontSize={10} />
              <Bar dataKey="new_visitors" name="New" stackId="a" fill={colour(0)} />
              <Bar dataKey="returning_visitors" name="Returning" stackId="a" fill={colour(1)} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

/* ─────────────── 9. Fraud ─────────────── */

function FraudTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficFraud()
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="space-y-4">
      <p className="font-body text-body-sm text-text-secondary">
        Sessions flagged for high bot / threat score OR parsed as bots. Last {data.days} days.
        {data.alerts.length === 0 ? ' No alerts in this window.' : ''}
      </p>
      {data.alerts.length > 0 ? (
        <TableShell>
          <table className="w-full font-body text-body-sm">
            <thead className="bg-parchment/50 text-text-secondary">
              <tr>
                <Th>Session</Th><Th className="text-right">Bot</Th><Th className="text-right">Threat</Th>
                <Th>ASN</Th><Th>Country</Th><Th className="text-right">PVs</Th><Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {data.alerts.map((a) => (
                <tr key={a.session_id} className="border-t border-border">
                  <Td className="font-mono text-body-xs">{a.session_id.slice(0, 8)}</Td>
                  <Td className="text-right font-mono">{a.bot_score !== null ? a.bot_score.toFixed(2) : '—'}</Td>
                  <Td className="text-right font-mono">{a.threat_score !== null ? a.threat_score : '—'}</Td>
                  <Td className="text-text-secondary">{a.as_organization ?? '—'}</Td>
                  <Td>{a.country ?? '—'}</Td>
                  <Td className="text-right font-mono">{a.page_view_count}</Td>
                  <Td className="whitespace-nowrap font-mono text-body-xs">{a.last_seen_at.slice(0, 19).replace('T', ' ')}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableShell>
      ) : null}
    </div>
  )
}

/* ─────────────── 10. Sessions + drill-down ─────────────── */

function SessionsTab(): JSX.Element {
  const { data, isLoading, error, reload } = useTrafficSessions()
  const [drillId, setDrillId] = useState<string | null>(null)
  if (isLoading) return <LoadingBlock />
  if (error !== null) return <ErrorBlock message={error} onRetry={reload} />
  if (data === null) return <LoadingBlock />
  return (
    <div className="space-y-4">
      <p className="font-body text-body-sm text-text-secondary">
        Last {data.days} days · {data.total} sessions total · showing {data.items.length}.
      </p>
      <TableShell>
        <table className="w-full font-body text-body-sm">
          <thead className="bg-parchment/50 text-text-secondary">
            <tr>
              <Th>First seen</Th><Th>Country · City</Th><Th>Device</Th>
              <Th className="text-right">PVs</Th><Th>Last page</Th><Th>Auth</Th><Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((s) => (
              <tr key={s.session_id} className="border-t border-border">
                <Td className="whitespace-nowrap font-mono text-body-xs">{s.first_seen_at.slice(0, 19).replace('T', ' ')}</Td>
                <Td>{s.country ?? '—'}{s.city ? ` · ${s.city}` : ''}</Td>
                <Td>{s.device_type ?? '—'}{s.browser ? ` · ${s.browser}` : ''}</Td>
                <Td className="text-right font-mono">{s.page_view_count}</Td>
                <Td className="font-mono max-w-[24ch] truncate" title={s.last_page_path ?? ''}>{s.last_page_path ?? '—'}</Td>
                <Td>{s.is_authenticated === 1 ? '✓' : ''}</Td>
                <Td>
                  <Button size="sm" variant="ghost" onClick={() => setDrillId(s.session_id)}>Details</Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
      <SessionDrillModal sessionId={drillId} onClose={() => setDrillId(null)} />
    </div>
  )
}

function SessionDrillModal({ sessionId, onClose }: { sessionId: string | null; onClose: () => void }): JSX.Element {
  const { data, isLoading, error } = useTrafficSessionDetail(sessionId)
  return (
    <Modal open={sessionId !== null} onClose={onClose} title="Session detail" maxWidth="lg">
      {isLoading ? <Spinner /> : null}
      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {data !== null ? (
        <div className="space-y-4">
          <section className="rounded-md border border-border bg-parchment/40 p-3 grid grid-cols-2 gap-2 font-body text-body-xs">
            <div><span className="text-text-secondary">Session:</span> <span className="font-mono">{data.session.session_id}</span></div>
            <div><span className="text-text-secondary">Customer:</span> <span className="font-mono">{data.session.customer_id ?? '(anon)'}</span></div>
            <div><span className="text-text-secondary">First seen:</span> {data.session.first_seen_at}</div>
            <div><span className="text-text-secondary">Last seen:</span> {data.session.last_seen_at}</div>
            <div><span className="text-text-secondary">Geo:</span> {data.session.country ?? '—'}{data.session.city ? ` · ${data.session.city}` : ''}</div>
            <div><span className="text-text-secondary">Device:</span> {data.session.device_type ?? '—'} · {data.session.browser ?? '—'} · {data.session.os ?? '—'}</div>
            <div><span className="text-text-secondary">Viewport:</span> {data.session.viewport_width ?? '—'}×{data.session.viewport_height ?? '—'} · {data.session.connection_type ?? '—'}</div>
            <div><span className="text-text-secondary">ASN:</span> {data.session.as_organization ?? '—'}</div>
            <div><span className="text-text-secondary">First referer:</span> {data.session.first_referer_host ?? '(direct)'}</div>
            <div><span className="text-text-secondary">First UTM:</span> {data.session.first_utm_source ?? '—'} / {data.session.first_utm_campaign ?? '—'}</div>
            <div><span className="text-text-secondary">Landing:</span> <span className="font-mono">{data.session.last_page_path ?? '—'}</span></div>
            <div><span className="text-text-secondary">Exit:</span> <span className="font-mono">{data.session.exit_page_path ?? '—'}</span></div>
          </section>
          <section>
            <p className="font-body text-body-sm text-text-secondary mb-2">Page timeline ({data.pageviews.length} views)</p>
            <TableShell>
              <table className="w-full font-body text-body-xs">
                <thead className="bg-parchment/50 text-text-secondary">
                  <tr><Th>#</Th><Th>Entered</Th><Th>Path</Th><Th className="text-right">Dwell</Th><Th>Exit</Th></tr>
                </thead>
                <tbody>
                  {data.pageviews.map((p, i) => (
                    <tr key={i} className="border-t border-border">
                      <Td className="font-mono">{i + 1}</Td>
                      <Td className="whitespace-nowrap font-mono">{p.entered_at.slice(11, 19)}</Td>
                      <Td className="font-mono max-w-[24ch] truncate" title={p.path}>{p.path}</Td>
                      <Td className="text-right font-mono">{p.dwell_ms === null ? '—' : `${(p.dwell_ms / 1000).toFixed(1)}s`}</Td>
                      <Td>{p.is_exit === 1 ? '✓' : ''}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableShell>
          </section>
        </div>
      ) : null}
    </Modal>
  )
}
